import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import { ESCAPE_PRIORITY, registerEscapeHandler } from '@peruselab/structure-editor/components';
import { useTranslation } from 'react-i18next';
import { scrollParent } from '../hooks/useLoadMoreOnApproach';
import {
  TOUCH_HOLD_MS,
  autoScrollSpeed,
  exceededTouchSlop,
  passedDragThreshold,
  pickTarget,
  startsOnControl,
  type DragPayload,
  type Point
} from './dragDropModel';

export type { DragPayload } from './dragDropModel';

/** Where a drag was dropped, in client (viewport) pixels. */
export interface DropPoint {
  readonly clientX: number;
  readonly clientY: number;
}

interface DropTargetEntry {
  readonly element: Element;
  readonly label: string;
  readonly accepts: (payload: DragPayload) => boolean;
  readonly onDrop: (payload: DragPayload, point: DropPoint) => void;
}

interface ActiveDrag {
  readonly payload: DragPayload;
  readonly preview: ReactNode;
  readonly overId: string | null;
}

/** What never changes while the workspace is open: sources and targets use it without
 *  re-rendering when a drag starts, moves over a target, or ends. */
interface DragDropActions {
  readonly register: (id: string, entry: DropTargetEntry) => () => void;
  readonly press: (event: ReactPointerEvent<HTMLElement>, payload: DragPayload, preview: ReactNode) => void;
  /** True while a touch drag is under way: the sources' own touch listeners then stop scrolling. */
  readonly touchDragging: { readonly current: boolean };
}

const DragDropActionsContext = createContext<DragDropActions | null>(null);
/** The drag under way, if any -- only drop targets read it (to highlight). */
const ActiveDragContext = createContext<ActiveDrag | null>(null);

/**
 * #699 -- drag and drop across the workspace, on pointer events (see dragDropModel.ts for the
 * rules). Sources (useDragSource) carry a payload; targets (useDropTarget) say which payloads they
 * accept and are told where it landed. While dragging, a preview follows the pointer, the target
 * under it is highlighted, the results column scrolls near its edges, and a live region says what
 * is happening. Escape, or letting go anywhere but a target, cancels.
 *
 * Hand-rolled on purpose (owner, 2026-09-27; see docs/drag-and-drop.md): the preview is moved by
 * writing its transform directly, so a pointer move re-renders nothing -- only entering or leaving
 * a target does. A dnd-kit version was tried and felt slower. Keyboard and touch users also have
 * "Add to paper".
 */
export function DragDropProvider({ children }: { readonly children: ReactNode }) {
  const { t } = useTranslation();
  const targets = useRef(new Map<string, DropTargetEntry>());
  const busy = useRef(false);
  const touchDragging = useRef(false);
  const pointer = useRef<Point>({ x: 0, y: 0 });
  const ghost = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<ActiveDrag | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const register = useCallback((id: string, entry: DropTargetEntry) => {
    targets.current.set(id, entry);
    return () => {
      targets.current.delete(id);
    };
  }, []);

  const placeGhost = useCallback(() => {
    if (ghost.current) ghost.current.style.transform = `translate(${pointer.current.x}px, ${pointer.current.y}px)`;
  }, []);
  // The preview mounts one render after the drag starts; put it under the pointer before paint.
  useLayoutEffect(placeGhost, [drag?.payload, placeGhost]);

  const press = useCallback(
    (event: ReactPointerEvent<HTMLElement>, payload: DragPayload, preview: ReactNode) => {
      if (busy.current || event.button !== 0 || !event.isPrimary || startsOnControl(event.target, event.currentTarget))
        return;
      busy.current = true;
      const { pointerId } = event;
      const touch = event.pointerType === 'touch';
      const start = { x: event.clientX, y: event.clientY };
      const scroller = scrollParent(event.currentTarget);
      let dragging = false;
      let over: string | null = null;
      let frame = 0;

      const targetUnder = (point: Point) =>
        pickTarget(
          [...targets.current.entries()]
            .filter(([, target]) => target.accepts(payload))
            .map(([id, target]) => {
              const { left, top, right, bottom } = target.element.getBoundingClientRect();
              return { id, left, top, right, bottom };
            }),
          point
        );
      const moveTo = (point: Point) => {
        pointer.current = point;
        placeGhost();
        const next = targetUnder(point);
        if (next === over) return;
        over = next;
        setDrag((current) => current && { ...current, overId: next });
      };
      const autoScroll = () => {
        const { x, y } = pointer.current;
        if (scroller) {
          const rect = scroller.getBoundingClientRect();
          const speed = x >= rect.left && x <= rect.right ? autoScrollSpeed(rect, y) : 0;
          if (speed !== 0) {
            scroller.scrollTop += speed;
            moveTo(pointer.current);
          }
        }
        frame = requestAnimationFrame(autoScroll);
      };
      const begin = () => {
        dragging = true;
        touchDragging.current = touch;
        pointer.current = start;
        document.documentElement.classList.add('dnd-dragging');
        setDrag({ payload, preview, overId: null });
        setAnnouncement(t('dragDrop.started', { label: payload.label }));
        moveTo(start);
        frame = requestAnimationFrame(autoScroll);
      };
      const hold = touch ? window.setTimeout(begin, TOUCH_HOLD_MS) : 0;
      // Touch events go to the element first touched, even if it leaves the page mid-gesture (then
      // they no longer bubble to the source): stop scrolling on that element itself too.
      const touched = touch && event.target instanceof Element ? event.target : null;
      const blockScroll = (touchMove: Event) => {
        if (dragging) touchMove.preventDefault();
      };
      touched?.addEventListener('touchmove', blockScroll, { passive: false });

      const onMove = (move: PointerEvent) => {
        if (move.pointerId !== pointerId) return;
        const point = { x: move.clientX, y: move.clientY };
        if (dragging) {
          moveTo(point);
        } else if (touch) {
          if (exceededTouchSlop(start, point)) end(false); // a swipe: let the results scroll
        } else if (passedDragThreshold(start, point)) {
          begin();
          moveTo(point);
        }
      };
      const onUp = (up: PointerEvent) => {
        if (up.pointerId === pointerId) end(true);
      };
      const onCancel = (cancel: PointerEvent) => {
        if (cancel.pointerId === pointerId) end(false);
      };
      // #762 -- Escape cancels a drag in progress (gesture first in the escape arbiter).
      const releaseEscape = registerEscapeHandler(ESCAPE_PRIORITY.gesture, () => {
        if (!dragging) return false;
        end(false);
        return true;
      });
      // A long press must not open a menu. (Scrolling is stopped by the source's own touch listener.)
      const onContextMenu = (menu: Event) => {
        if (touch) menu.preventDefault();
      };

      function end(drop: boolean) {
        window.clearTimeout(hold);
        cancelAnimationFrame(frame);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        releaseEscape();
        window.removeEventListener('contextmenu', onContextMenu, true);
        touched?.removeEventListener('touchmove', blockScroll);
        busy.current = false;
        touchDragging.current = false;
        if (!dragging) return;
        document.documentElement.classList.remove('dnd-dragging');
        setDrag(null);
        // The release would otherwise click whatever it landed on (a card's own buttons).
        const swallow = (click: MouseEvent) => click.stopPropagation();
        window.addEventListener('click', swallow, { capture: true, once: true });
        window.setTimeout(() => window.removeEventListener('click', swallow, true), 0);
        const target = drop && over ? targets.current.get(over) : undefined;
        if (!target) {
          setAnnouncement(t('dragDrop.cancelled', { label: payload.label }));
          return;
        }
        setAnnouncement(t('dragDrop.dropped', { label: payload.label, target: target.label }));
        target.onDrop(payload, { clientX: pointer.current.x, clientY: pointer.current.y });
      }

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
      window.addEventListener('contextmenu', onContextMenu, true);
    },
    [placeGhost, t]
  );

  const actions = useMemo(() => ({ register, press, touchDragging }), [register, press]);
  return (
    <DragDropActionsContext.Provider value={actions}>
      <ActiveDragContext.Provider value={drag}>
        {children}
        {drag
          ? createPortal(
              <div ref={ghost} className="dnd-ghost" data-testid="dnd-ghost" aria-hidden="true">
                {drag.preview}
              </div>,
              document.body
            )
          : null}
        <div className="visually-hidden" aria-live="polite" data-testid="dnd-announcer">
          {announcement}
        </div>
      </ActiveDragContext.Provider>
    </DragDropActionsContext.Provider>
  );
}

/**
 * Makes an element draggable: spread the result onto it (it includes the element's `ref`).
 * `payload` null = not draggable now. Outside a DragDropProvider it does nothing.
 *
 * Touch (owner report, #700 -- real phones, tablets and touch laptops): the browser decides at
 * touch-start whether a gesture may scroll, from the touch listeners present *then*. A listener
 * added once the drag begins is too late: the first move scrolls, and the browser cancels the
 * drag. So every source carries its own non-passive touchmove listener from the start, which
 * blocks scrolling only while a touch drag is under way -- a swipe over a card still scrolls.
 */
export function useDragSource(payload: DragPayload | null, preview: ReactNode) {
  const actions = useContext(DragDropActionsContext);
  const press = actions?.press;
  const touchDragging = actions?.touchDragging;
  const [node, setNode] = useState<Element | null>(null);
  useEffect(() => {
    if (!node || !touchDragging) return;
    const blockScroll = (event: Event) => {
      if (touchDragging.current) event.preventDefault();
    };
    node.addEventListener('touchmove', blockScroll, { passive: false });
    return () => node.removeEventListener('touchmove', blockScroll);
  }, [node, touchDragging]);
  const latest = useRef({ payload, preview });
  useEffect(() => {
    latest.current = { payload, preview };
  });
  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (latest.current.payload) press?.(event, latest.current.payload, latest.current.preview);
    },
    [press]
  );
  return press && payload ? { ref: setNode, onPointerDown, 'data-draggable': true } : {};
}

/**
 * Makes an element a drop target: pass `setNode` as its ref. `canDrop` is true while an acceptable
 * drag is under way (to hint where it can go), `isOver` while it is over this target.
 */
export function useDropTarget({
  label,
  accepts,
  onDrop
}: {
  readonly label: string;
  readonly accepts: (payload: DragPayload) => boolean;
  readonly onDrop: (payload: DragPayload, point: DropPoint) => void;
}) {
  const actions = useContext(DragDropActionsContext);
  const drag = useContext(ActiveDragContext);
  const id = useId();
  // Any element: an HTML block, or an SVG shape (a reaction box on the paper).
  const [element, setElement] = useState<Element | null>(null);
  const latest = useRef({ label, accepts, onDrop });
  useEffect(() => {
    latest.current = { label, accepts, onDrop };
  });
  const register = actions?.register;
  useEffect(() => {
    if (!register || !element) return;
    return register(id, {
      element,
      get label() {
        return latest.current.label;
      },
      accepts: (payload) => latest.current.accepts(payload),
      onDrop: (payload, point) => latest.current.onDrop(payload, point)
    });
  }, [element, id, register]);
  return {
    setNode: setElement,
    canDrop: drag !== null && accepts(drag.payload),
    isOver: drag?.overId === id
  };
}
